import { useState } from 'react';
import Image from 'next/image';
import { Card, CardContent } from '@/components/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/components/ui/tabs';
import EmailSignupField from '@/components/HomePageV2/EmailSignupField/EmailSignupField';
import { useRouter } from 'next/router';

const CreateVideoRows = (activeCatalog, handleVideoCardClick) => {
    const videos = activeCatalog.videos.slice(0, 3);
    return (
        <div className=''>
            <div className='grid grid-flow-row grid-cols-1 lg:grid-cols-3 gap-6 mt-6'>
                {videos.map((video, index) => (
                    <Card
                        key={video.slug}
                        style={{
                            boxShadow: '0 4px 16px 0 rgba(44, 41, 92, 0.12), 0 1.5px 4px 0 rgba(44, 41, 92, 0.10)'
                        }}
                        className='md:overflow-hidden min-w-[300px] md:min-w-full hover:cursor-pointer rounded-[12px] border-[1px] border-solid border-[#D9D9D9]'
                        onClick={() => handleVideoCardClick(video.slug)}
                    >
                        <CardContent className='p-0'>
                            <div className='px-3.5 pt-3.5 pb-7 grid grid-cols-2 '>
                                <div className='relative aspect-video w-[125%] sm:w-full xl:w-[125%] max-w-[125%]sm:max-w-full xl:max-w-[125%] '>
                                    <Image
                                        src={video.thumbnail.url || '/placeholder.svg'}
                                        alt={video.title}
                                        className='object-cover rounded-[12px]'
                                        layout='fill'
                                        onError={({ currentTarget }) => {
                                            currentTarget.onerror = null; // prevent looping
                                            currentTarget.src = '/placeholder.svg';
                                        }}
                                    />
                                </div>
                                <div
                                    className='flex items-center justify-center bg-[#1E1E1E] text-white rounded-[100%] text-2xl sm:text-[40px] w-full max-w-[55px] sm:max-w-[92px] max-h-[55px] sm:max-h-[92px] ml-auto'
                                    style={index === 0 ? { backgroundColor: '#1CC8E1' } : {}}
                                >
                                    # {index + 1}
                                </div>
                            </div>

                            <div className='p-5 bg-[#4E389D] text-white w-full rounded-b-[12px]'>
                                <p className='font-medium my-0 text-base line-clamp-1'>{video.title}</p>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
};

export default function VideoLibraryComponent({ hideBlur = false, videoGroups, setRedirectModal, setInactiveAccount }) {
    const sortedVideoGroups = videoGroups.sort((a, b) => a.index - b.index);
    const [activeCatalog, setActiveCatalog] = useState(sortedVideoGroups[0]);
    const router = useRouter();

    const handleVideoCardClick = (videoSlug: any) => {
        router.push(`/lessons/${videoSlug}`);
    };

    return (
        <div className='relative px-4 md:px-[70px] mt-20 sm:mt-[110px] z-[2]'>
            {/* blur effect pink */}
            {!hideBlur && (
                <div className='hidden md:block absolute top-[60px] left-0 rounded-full opacity-[1.4] bg-[#B031FA] bg-opacity-[0.7] blur-[250px] w-[729px] h-[585px] z-[1]' />
            )}
            {/* blur effect dark purple */}
            {!hideBlur && (
                <div className='hidden md:block absolute top-[-70px] right-0 rounded-full opacity-[1.4] bg-[#161042] bg-opacity-[0.7] blur-[250px] w-[729px] h-[585px] z-[1]' />
            )}
            <div className='max-w-[1171px] mx-auto space-y-8 relative z-[2] mt-10'>
                {/* Header */}
                <div className='text-center relative z-[2] text-white '>
                    <h1 className=' text-[50px] xl:text-[60px] leading-[50px] xl:leading-[60px] font-normal my-0 hidden lg:block max-h-[110px] mb-2'>
                        Math that <span className='text-[#FFDB35] font-Gloria'>sticks.</span> Music that{' '}
                        <span className='text-[#64DFE5] font-Gloria'>rocks.</span>
                    </h1>
                    <div className='block lg:hidden mb-2'>
                        <h1 className='text-[40px] font-normal md:text-[50px] my-0'>
                            Math that <span className='text-[#FFDB35] font-Gloria'>sticks.</span>
                        </h1>
                        <h1 className='text-[40px] font-normal md:text-[50px] my-0 leading-6'>
                            Music that <span className='text-[#64DFE5] font-Gloria'>rocks.</span>
                        </h1>
                    </div>
                    <div className='text-xl font-normal '>
                        <p className='my-0 leading-6 md:leading-[1.85rem] max-w-[800px] mx-auto mt-6'>
                            Watch some of our most popular videos and see why Muzology is fast-tracking math success.
                        </p>
                    </div>
                </div>
            </div>
            <div className='mx-auto max-w-[1300px] bg-white rounded-[18px] mt-[32px] relative z-[2]'>
                <div className='text-2xl font-semibold text-[#2D1B69] my-0 text-center sm:hidden pt-8'>The Muzology Charts</div>
                <div className='text-2xl font-semibold text-[#2D1B69] my-0 text-center sm:hidden'>Top 3 Hits by Math Genre</div>
                <div className='container mx-auto px-6 pb-8 sm:py-8 space-y-10 '>
                    <div className='text-2xl font-semibold text-[#2D1B69] my-0 text-center hidden sm:block'>The Muzology Charts - Top 3 Hits by Math Genre</div>

                    <Tabs defaultValue={activeCatalog.title} className='w-full '>
                        <TabsList className='bg-[rgba(57, 35, 112, 0.05)] gap-2'>
                            {videoGroups.map((catalog) => (
                                <TabsTrigger
                                    key={catalog.title}
                                    value={catalog.title}
                                    className='data-[state=active]:bg-[#4E389D] data-[state=active]:text-white rounded-full px-6 py-3 border-none font-normal'
                                    onClick={() => setActiveCatalog(catalog)}
                                >
                                    {catalog.title}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                    {CreateVideoRows(activeCatalog, handleVideoCardClick)}

                    <div className='text-center space-x-6 flex flex-col lg:flex-row items-center mt-5'>
                        <p className='text-lg text-center sm:text-left font-normal text-[#1C0057] my-0 w-full lg:w-1/2 '>
                            Unlock these lessons and much more with the <span className='font-bold'>Complete Math Mastery Plan</span>, covering 70 critical math
                            topics for Grades 3-8. <span className='font-bold'>Try it Free Now!</span>
                        </p>
                        {/* Email capture  */}
                        <div className='w-full lg:w-1/2'>
                            <EmailSignupField
                                className='flex flex-col md:flex-row justify-center items-center gap-4 w-full mt-10 lg:mt-0'
                                inputClassName='text-[18px] w-full max-w-[260px] md:max-w-[300px] pl-0 md:pl-4 py-[26px] bg-gray-50  border-[1px] border-solid border-[#1C0057] text-[#1C0057] font-normal rounded-full text-center md:text-left'
                                setRedirectModal={setRedirectModal}
                                setInactiveAccount={setInactiveAccount}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
